import { ChevronLeft, ChevronRight, ChevronsUpDown, LogOut, Store, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { SearchSection } from './merchant-switcher/search-section';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar';
import { useAuth, useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import { Link, useParams } from 'react-router-dom';
import { Tabs, TabsContent } from '../ui/tabs';
import { useState, useMemo } from 'react';
import { Avatar, AvatarImage } from '../ui/avatar';
import type { OrganizationMembershipResource } from '@clerk/types';
import { useRecentMerchants } from './hooks/use-recent-merchants';
import { MAX_VISIBLE_MERCHANTS } from './config';

export function MerchantSwitcher() {
  const [activeTab, setActiveTab] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const { slug } = useParams<{ slug: string }>();
  const { isMobile } = useSidebar();
  const { isLoaded: isUserLoaded, user } = useUser();
  const { organization: currentOrg } = useOrganization();

  const {
    isLoaded: isMembershipsLoaded,
    userMemberships,
    setActive
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: 100
    }
  });
  const { signOut } = useAuth();
  const { addRecentMerchant, getSortedMerchants } = useRecentMerchants();

  // Sort merchants with recently accessed ones first
  const sortedMerchants = useMemo(() => {
    if (!isMembershipsLoaded || userMemberships.data.length === 0) {
      return [];
    }
    return getSortedMerchants(userMemberships.data, currentOrg?.id);
  }, [isMembershipsLoaded, userMemberships.data, getSortedMerchants, currentOrg?.id]);

  const filteredMerchants = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedMerchants.slice(0, MAX_VISIBLE_MERCHANTS);
    }

    const query = searchQuery.toLowerCase().trim();
    return sortedMerchants
      .filter(
        (membership) =>
          membership.organization.name.toLowerCase().includes(query) ||
          membership.organization.slug?.toLowerCase().includes(query)
      )
      .slice(0, MAX_VISIBLE_MERCHANTS);
  }, [sortedMerchants, searchQuery]);

  const handleMerchantSwitch = async (
    e: React.MouseEvent,
    membership: OrganizationMembershipResource
  ) => {
    e.preventDefault();

    // Add to recent merchants
    addRecentMerchant(membership);

    if (!setActive) {
      console.log('Fallback to direct navigation with set active');
      // Fallback to direct navigation if setActive is not available
      window.location.replace(`/merchants/${membership.organization.slug!}/dashboard`);
      return;
    }
    await setActive({ organization: membership.organization.id });

    // Now navigate to the new merchant dashboard
    window.location.replace(`/merchants/${membership.organization.slug!}/dashboard`);
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    if (newTab === 'merchants') {
      setSearchQuery(''); // Clear search when opening merchants tab
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu
          onOpenChange={(open) => {
            if (!open) {
              setActiveTab('default');
              setSearchQuery(''); // Clear search when closing dropdown
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton className="data-[state=open]:bg-accent hover:bg-accent/80 group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon] h-auto flex p-1 data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-1">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg text-sidebar-primary-foreground">
                <img
                  src={currentOrg?.imageUrl}
                  alt={currentOrg?.name}
                  className="size-8 rounded-lg"
                />
              </div>
              <div className="grid flex-1 text-left text-sm leading-normal">
                <span className="truncate">{currentOrg?.name}</span>
              </div>
              <ChevronsUpDown />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsContent value="default" className="mt-0">
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('merchants');
                  }}
                >
                  <div className="flex size-6 items-center justify-center rounded-md">
                    <Store className="size-4" />
                  </div>
                  <div className="flex-1">Switch Merchant</div>
                  <ChevronRight className="size-4" />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {isUserLoaded && user && (
                  <DropdownMenuItem className="gap-2 cursor-pointer" asChild>
                    <Link to={`/merchants/${slug!}/settings/profile`}>
                      <div>
                        <Avatar className="size-4 mx-1">
                          <AvatarImage src={user.imageUrl} alt={user.username!} />
                        </Avatar>
                      </div>
                      <div>{user.primaryEmailAddress?.emailAddress}</div>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onClick={() => {
                    void signOut();
                  }}
                >
                  <div className="flex size-6 items-center justify-center rounded-md">
                    <LogOut className="size-4" />
                  </div>
                  <div>Logout</div>
                </DropdownMenuItem>
              </TabsContent>
              <TabsContent value="merchants" className="mt-0">
                <DropdownMenuItem
                  className="gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('default');
                  }}
                >
                  <div className="flex size-4 items-center justify-center rounded-sm">
                    <ChevronLeft className="size-4" />
                  </div>
                  <div className="flex-1">Back</div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {isMembershipsLoaded && userMemberships.data.length > MAX_VISIBLE_MERCHANTS && (
                  <SearchSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                )}

                <div className="p-1 flex flex-col flex-start">
                  <h5 className="text-xs font-medium text-muted-foreground p-[6px]">
                    Recently Accessed Stores
                  </h5>

                  {isMembershipsLoaded && (
                    <>
                      {filteredMerchants.length > 0 ? (
                        filteredMerchants.map((membership, _) => (
                          <DropdownMenuItem
                            key={membership.organization.name}
                            className="flex p-[6px] px-2 items-center gap-2 self-stretch cursor-pointer hover:bg-accent/80"
                            asChild
                          >
                            <Link
                              onClick={(e) => {
                                void handleMerchantSwitch(e, membership);
                              }}
                              to={'javascript:void(0)'}
                            >
                              <div className="flex items-center gap-[10px] min-w-0">
                                <div className="w-4 flex justify-center flex-shrink-0">
                                  {membership.organization.id === currentOrg?.id && (
                                    <Check size={16} className="text-foreground" />
                                  )}
                                </div>
                                <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap min-w-0 text-sm">
                                  {membership.organization.name}
                                </div>
                              </div>
                            </Link>
                          </DropdownMenuItem>
                        ))
                      ) : (
                        <div className="px-2 py-3 text-center text-sm text-muted-foreground">
                          No merchants found matching &quot;{searchQuery}&quot;
                        </div>
                      )}
                    </>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
